import { useEffect, useRef, useState } from "react";
import { Sparkles, Upload, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/site/StatusBadge";
import { imgSrc } from "@/pages/BlogPages";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { title: "", slug: "", meta_description: "", keywords: [], excerpt: "", content: "", faq: [], cover_image: "", cover_alt: "", status: "draft" };
const faqToText = (faq) => faq.map((f) => `${f.q} | ${f.a}`).join("\n");
const textToFaq = (t) => t.split("\n").map((l) => l.split("|")).filter((p) => p.length > 1 && p[0].trim()).map(([q, ...a]) => ({ q: q.trim(), a: a.join("|").trim() }));

function AiWriter({ onDraft }) {
  const [subject, setSubject] = useState("");
  const [kw, setKw] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { subject, keywords: kw.split(",").map((k) => k.trim()).filter(Boolean) });
      onDraft({ ...EMPTY, ...data });
      toast.success("Draft written — review, add an image and publish");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#F08A3C]/40 bg-gradient-to-br from-[#FFF7EF] to-white p-5 flex flex-col gap-3" data-testid="blog-ai-writer">
      <p className="flex items-center gap-2 font-semibold text-[#0B2A4A]"><Sparkles className="h-4 w-4 text-[#F08A3C]" /> AI Blog Writer</p>
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject, e.g. Best beaches in Nicaragua for remote workers" data-testid="blog-ai-subject" />
      <Input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="Target keywords, comma separated (first = main keyword)" data-testid="blog-ai-keywords" />
      <button onClick={go} disabled={busy || subject.trim().length < 3} className="self-start rounded-xl bg-[#F08A3C] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="blog-ai-generate">
        {busy ? "Writing your article (about 1 minute)..." : "Write article with AI"}
      </button>
    </div>
  );
}

function Editor({ post, onSaved, onCancel }) {
  const [f, setF] = useState({ ...post, faqText: faqToText(post.faq || []), kwText: (post.keywords || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const fl = e.target.files?.[0];
    e.target.value = "";
    if (!fl) return;
    const fd = new FormData();
    fd.append("file", fl);
    try {
      const { data } = await api.post("/admin/uploads", fd);
      setF((x) => ({ ...x, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const save = async (status) => {
    setBusy(true);
    const body = { ...f, status, faq: textToFaq(f.faqText), keywords: f.kwText.split(",").map((k) => k.trim()).filter(Boolean) };
    delete body.faqText; delete body.kwText; delete body.id; delete body.word_count; delete body.created_at; delete body.updated_at; delete body.published_at; delete body.author;
    try {
      await (post.id ? api.put(`/admin/blog/${post.id}`, body) : api.post("/admin/blog", body));
      toast.success(status === "published" ? "Published" : "Saved as draft");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const words = (f.content.match(/\w+/g) || []).length;
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 flex flex-col gap-3" data-testid="blog-editor">
      <Input value={f.title} onChange={set("title")} placeholder="SEO title (under 60 characters)" data-testid="blog-title-input" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Input value={f.slug} onChange={set("slug")} placeholder="url-slug" data-testid="blog-slug-input" />
        <Input value={f.kwText} onChange={set("kwText")} placeholder="Keywords, comma separated" data-testid="blog-keywords-input" />
      </div>
      <Textarea rows={2} value={f.meta_description} onChange={set("meta_description")} placeholder="Meta description (140–155 characters)" data-testid="blog-meta-input" />
      <p className="text-xs text-slate-500 -mt-2">{f.meta_description.length} characters</p>
      <Textarea rows={2} value={f.excerpt} onChange={set("excerpt")} placeholder="Short excerpt for the blog list" data-testid="blog-excerpt-input" />
      <div className="flex flex-wrap items-center gap-3">
        {f.cover_image && <img src={imgSrc(f.cover_image)} alt="" className="h-20 w-32 object-cover rounded-lg" data-testid="blog-cover-preview" />}
        <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="blog-cover-file" />
        <button type="button" onClick={() => file.current?.click()} className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 px-4 py-2 text-sm font-semibold" data-testid="blog-cover-upload-btn"><Upload className="h-4 w-4" /> Upload cover image</button>
        <Input value={f.cover_alt} onChange={set("cover_alt")} placeholder="Image description (alt text)" className="flex-1 min-w-[200px]" data-testid="blog-alt-input" />
      </div>
      <Textarea rows={16} value={f.content} onChange={set("content")} placeholder={"Article body. Use ## for H2 and ### for H3 headings, - for bullets."} className="font-mono text-sm" data-testid="blog-content-input" />
      <p className="text-xs text-slate-500 -mt-2">{words} words {words < 900 && "· aim for 1,000+ words for SEO"}</p>
      <Textarea rows={4} value={f.faqText} onChange={set("faqText")} placeholder="FAQ — one per line: Question | Answer" data-testid="blog-faq-input" />
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => save("published")} className="rounded-xl bg-emerald-600 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="blog-publish-btn">Publish</button>
        <button disabled={busy} onClick={() => save("draft")} className="rounded-xl border border-[#0B2A4A]/20 px-5 py-2.5 text-sm font-semibold" data-testid="blog-draft-btn">Save draft</button>
        <button onClick={onCancel} className="rounded-xl px-4 py-2.5 text-sm text-slate-500" data-testid="blog-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

export function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = (p) => window.confirm(`Delete "${p.title}"?`) && api.delete(`/admin/blog/${p.id}`).then(() => { toast.success("Deleted"); load(); });
  return (
    <div className="flex flex-col gap-6" data-testid="blog-admin">
      {editing ? <Editor post={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /> : (
        <>
          <AiWriter onDraft={setEditing} />
          <button onClick={() => setEditing(EMPTY)} className="self-start inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2 text-sm font-semibold" data-testid="blog-new-btn"><Plus className="h-4 w-4" /> Write my own article</button>
          <div className="flex flex-col gap-3">
            {posts.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-4 flex flex-wrap items-center gap-4" data-testid="blog-admin-row">
                {p.cover_image && <img src={imgSrc(p.cover_image)} alt="" className="h-14 w-20 object-cover rounded-lg" />}
                <div className="flex-1 min-w-[200px]"><p className="font-semibold text-[#0B2A4A]">{p.title}</p><p className="text-xs text-slate-500">/blog/{p.slug} · {p.word_count} words</p></div>
                <StatusBadge status={p.status === "published" ? "approved" : "pending"} />
                <div className="flex gap-2">
                  {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-lg border px-3 py-1.5 text-xs" aria-label="View"><ExternalLink className="h-3.5 w-3.5" /></a>}
                  <button onClick={() => setEditing(p)} className="rounded-lg bg-[#1A9AA8] text-white px-3 py-1.5 text-xs font-semibold" data-testid="blog-edit-btn">Edit</button>
                  <button onClick={() => remove(p)} className="rounded-lg border px-3 py-1.5 text-xs" aria-label="Delete" data-testid="blog-delete-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
